/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Table as AntTable,
  Button,
} from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import { checkToken } from 'api/utils';
import Select from 'components/Select';
import EditableTable from 'components/EditableTable';
import { onError } from 'utils/handlers';
import {
  getLiquidacionesEmpleadosDeducciones,
  getLiquidacionEmpleadoDeduccion,
  patchLiquidacionEmpleadoDeduccion,
} from 'api/nominas/liquidaciones/liquidacionesEmpleadosDeducciones';
import { formatReceivedValues } from 'utils/formatValues';
import CurrencyInput from 'components/CurrencyInput';
import currencyFormatter from 'utils/currencyFormatter';
import { getDeducciones } from 'api/nominas/deducciones/deducciones';
import {
  FLOAT_NUMER_PATTERN,
} from 'utils/patterns';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

export const liquidacionEmpleadoDeduccionPermission = {
  permissionModel: 'liquidacionempleadodeduccion',
  catalogsModels: [],
  tabsModels: [],
};

// eslint-disable-next-line react/prop-types, max-len, object-curly-newline
const LiquidacionesEmpleadosDeducciones = ({ permission, liquidacionEmpleado, datosEncabezado, onClickEditEncabezado }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [totalDeduccion, setTotalDeduccion] = useState(0);
  const [deducciones, setDeducciones] = useState();

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesEmpleadosDeducciones({ liquidacion_empleado: liquidacionEmpleado });
    setData(formatReceivedClone(_data));
    setTotalDeduccion(0);
    form.setFieldValue('importe_deducciones', 0);
    if (_data?.length) {
      let i = 0;
      let acum = 0;
      while (i < _data.length) {
        acum += _data[i].importe;
        i += 1;
      }
      setTotalDeduccion(acum);
      form.setFieldValue('importe_deducciones', acum);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _deducciones,
        _data,
      ] = await Promise.all([
        getDeducciones({ estados_globales: ESTATUS_AUTORIZADO }),
        await getLiquidacionesEmpleadosDeducciones({ liquidacion_empleado: liquidacionEmpleado }),
      ]);
      if (mounted) {
        setDeducciones(_deducciones);
        setData(_data);
        setTotalDeduccion(0);
        form.setFieldValue('importe_deducciones', 0);
        if (_data?.length) {
          let i = 0;
          let acum = 0;
          while (i < _data.length) {
            acum += _data[i].importe;
            i += 1;
          }
          setTotalDeduccion(acum);
          form.setFieldValue('importe_deducciones', acum);
        }
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _liquidacionEmpleadoIndemnizacion = await getLiquidacionEmpleadoDeduccion(match.id);
    setTimeout(() => {
      if (_liquidacionEmpleadoIndemnizacion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(_liquidacionEmpleadoIndemnizacion));
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        liquidacion_empleado: liquidacionEmpleado,
      };
      const [key] = selectedRowKeys;
      const saved = await patchLiquidacionEmpleadoDeduccion(key, values);
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
          await onClickEditEncabezado();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const handleOnRowClick = async (record) => {
    await form.validateFields();
    if (selectedRowKeys.length) {
      if (selectedRowKeys[0] === record.id) {
        return;
      }
      await onFinish();
    }
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Concepto',
      dataIndex: 'deduccion',
      key: 'deduccion',
      width: 300,
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <Form.Item
              name="deduccion"
              label="Deducciones"
              rules={rules.required}
              hasFeedback
            >
              <Select
                disabled
                dataSource={deducciones}
                render={(e) => `${e?.clave} - ${e?.concepto}`}
              />
            </Form.Item>
          );
        }
        const deduccionActual = deducciones.find((perc) => perc.id === val);
        return <p>{deduccionActual ? `${deduccionActual.clave} - ${deduccionActual.concepto}` : ''}</p>;
      },
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 200,
      align: 'right',
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <CurrencyInput
              name="importe"
              label="Importe"
              rules={rules.required}
            />
          );
        }
        return <p>{currencyFormatter(val || 0)}</p>;
      },
    },
  ];

  const summaryCells = [
    {
      label: 'Total',
      value: totalDeduccion,
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  // Detalle del calculo de ISR (solo presente en la Deduccion de ISR, una vez que la
  // Liquidacion se calcula/recalcula con el motor real -- ver calcular_ISR() en
  // nominas/serializers/liquidaciones_empleados.py).
  const columnasDetalleISRConceptos = [
    {
      title: 'Concepto', dataIndex: 'concepto', key: 'concepto', width: 220,
    },
    {
      title: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      title: 'Exento',
      dataIndex: 'exento',
      key: 'exento',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      title: 'Gravado',
      dataIndex: 'gravado',
      key: 'gravado',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  const columnasDetalleISRBaseGravable = [
    {
      title: 'Concepto', dataIndex: 'concepto', key: 'concepto', width: 220,
    },
    {
      title: 'Base Gravable',
      dataIndex: 'base_gravable',
      key: 'base_gravable',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      title: 'Límite Inferior',
      dataIndex: 'limite_inferior',
      key: 'limite_inferior',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      title: 'Excedente',
      dataIndex: 'excedente',
      key: 'excedente',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      title: '% de Excedente',
      dataIndex: 'porcentaje_excedente',
      key: 'porcentaje_excedente',
      align: 'right',
      render: (val) => `${Number(val || 0).toFixed(2)}%`,
    },
    {
      title: 'Impuesto Marginal',
      dataIndex: 'impuesto_marginal',
      key: 'impuesto_marginal',
      align: 'right',
      render: (val) => (val === null || val === undefined ? '-' : currencyFormatter(val || 0)),
    },
    {
      title: 'Cuota Fija',
      dataIndex: 'cuota_fija',
      key: 'cuota_fija',
      align: 'right',
      render: (val) => (val === null || val === undefined ? '-' : currencyFormatter(val || 0)),
    },
    {
      title: 'ISR Causado',
      dataIndex: 'isr_causado',
      key: 'isr_causado',
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      title: 'Tasa Real de Impuesto',
      dataIndex: 'tasa_real_impuesto',
      key: 'tasa_real_impuesto',
      align: 'right',
      render: (val) => (val === null || val === undefined ? '-' : `${(val * 100).toFixed(4)}%`),
    },
    {
      title: 'ISR Aportado',
      dataIndex: 'importe_isr',
      key: 'importe_isr',
      align: 'right',
      render: (val) => <b>{currencyFormatter(val || 0)}</b>,
    },
  ];

  const buildDetalleISRRows = (item) => {
    if (!item) return [];
    const fmt = (v) => (v === null || v === undefined ? null : Number(v));
    const rows = [];
    const isIrregular = item.promedio_diario !== null && item.promedio_diario !== undefined;

    if (isIrregular) {
      rows.push({
        key: 'concepto',
        label: item.concepto,
        valor: fmt(item.importe),
      });
      rows.push({
        key: 'exento',
        label: '( - )  Exento',
        valor: fmt(item.exento),
        extra: item.exento_umas_texto || null,
      });
      rows.push({
        key: 'gravable',
        label: '=  Importe Gravable',
        valor: fmt(item.gravado),
        bold: true,
      });
      rows.push({
        key: 'entre',
        label: '( / )  Entre',
        valor: 365,
      });
      rows.push({
        key: 'prom_diario',
        label: '=  Promedio Diario',
        valor: fmt(item.promedio_diario),
      });
      rows.push({
        key: 'por',
        label: '( x )  Por',
        valor: 30.4,
      });
      rows.push({
        key: 'prom_mensual',
        label: '=  Promedio Mensual',
        valor: fmt(item.promedio_mensual),
      });
      rows.push({
        key: 'sueldo_m',
        label: '( + )  Sueldo Mensual',
        valor: fmt(item.sueldo_mensual),
      });
      rows.push({
        key: 'base_grav',
        label: '=  Base Gravable',
        valor: fmt(item.base_gravable),
        bold: true,
      });
      rows.push({
        key: 'lim_inf',
        label: '( - )  Límite Inferior',
        valor: fmt(item.limite_inferior),
      });
      rows.push({
        key: 'excedente',
        label: '=  Excedente',
        valor: fmt(item.excedente),
      });
      rows.push({
        key: 'pct_exc',
        label: '( * )  % sobre Excedente',
        valor: fmt(item.porcentaje_excedente),
        format: 'pct',
      });
      rows.push({
        key: 'imp_marg',
        label: '=  Impuesto Marginal',
        valor: fmt(item.impuesto_marginal),
      });
      rows.push({
        key: 'cuota_fija',
        label: '( + )  Cuota Fija',
        valor: fmt(item.cuota_fija),
      });
      rows.push({
        key: 'isr_causado',
        label: '=  ISR Causado',
        valor: fmt(item.isr_causado),
        bold: true,
      });
      rows.push({
        key: 'subsidio',
        label: '( - )  Subsidio al Empleo',
        valor: fmt(item.subsidio_empleo),
      });
      rows.push({
        key: 'isr_causado2',
        label: 'ISR Causado',
        valor: fmt(item.isr_causado),
        bold: true,
      });
      rows.push({
        key: 'isr_sueldo',
        label: '( - )  ISR del Sueldo Mensual',
        valor: fmt(item.isr_sueldo_mensual),
      });
      rows.push({
        key: 'isr_adic',
        label: '=  ISR Adicional',
        valor: fmt(item.isr_adicional),
        bold: true,
      });
      rows.push({
        key: 'ing_mens',
        label: '( / )  Ingreso Mensualizado',
        valor: fmt(item.promedio_mensual),
      });
      rows.push({
        key: 'tasa_real',
        label: '=  Tasa Real de Impuesto',
        valor: fmt(item.tasa_real_impuesto),
        format: 'pct4',
        bold: true,
      });
      rows.push({
        key: 'imp_grav2',
        label: '( x )  Importe Gravable',
        valor: fmt(item.gravado),
      });
      rows.push({
        key: 'isr_final',
        label: `=  ISR de ${item.concepto}`,
        valor: fmt(item.importe_isr),
        bold: true,
        highlight: true,
      });
    }
    return rows;
  };

  const deduccionConDetalleISR = (data || []).find(
    (item) => Array.isArray(item?.detalle_isr) && item.detalle_isr.length > 0,
  );

  const exportarCedulaDeTrabajo = async () => {
    if (!deduccionConDetalleISR) return;
    const detalleISR = deduccionConDetalleISR.detalle_isr;
    const sueldoEntry = detalleISR.find(
      (d) => d.promedio_diario === null || d.promedio_diario === undefined,
    );
    const irregulares = detalleISR.filter(
      (d) => d.promedio_diario !== null && d.promedio_diario !== undefined,
    );
    if (!irregulares.length && !sueldoEntry) return;

    const workbook = new ExcelJS.Workbook();

    const thinBorder = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    const boldFont = {
      name: 'Calibri',
      size: 11,
      bold: true,
    };
    const normalFont = {
      name: 'Calibri',
      size: 11,
    };
    const fmtMoney = '#,##0.00';
    const fmtPct = '0.00%';
    const fmtPct4 = '0.0000%';
    const fmtDec6 = '#,##0.000000';
    const highlightFill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE6F7FF' },
    };
    const titleFill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF004080' },
    };
    const titleFont = {
      name: 'Calibri',
      size: 13,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };

    const resolverNombreHoja = (concepto) => {
      const c = (concepto || '').toLowerCase();
      if (c.includes('aguinaldo')) return 'ISR.AGUINALDO';
      if (c.includes('prima') || c.includes('vacacional')) return 'ISR.PRIMA.VACACIONAL';
      return `ISR.${concepto.toUpperCase().replace(/\s+/g, '.').substring(0, 31)}`;
    };

    const buildSheet = (item) => {
      const sheetName = resolverNombreHoja(item.concepto);
      const ws = workbook.addWorksheet(sheetName);

      ws.getColumn(1).width = 10;
      ws.getColumn(2).width = 42;
      ws.getColumn(3).width = 22;
      ws.getColumn(4).width = 22;
      ws.getColumn(5).width = 55;

      // -- Cabecera FINIQUITO (filas 1-10) --
      const headerFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF004080' },
      };
      const headerFont = {
        name: 'Calibri',
        size: 13,
        bold: true,
        color: { argb: 'FFFFFFFF' },
      };
      const labelFont = {
        name: 'Calibri',
        size: 11,
        bold: true,
      };
      const valueFont = {
        name: 'Calibri',
        size: 11,
      };

      ws.mergeCells('A1:E1');
      const finiquitoCell = ws.getCell('A1');
      finiquitoCell.value = 'FINIQUITO';
      finiquitoCell.font = headerFont;
      finiquitoCell.fill = headerFill;
      finiquitoCell.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.getRow(1).height = 28;

      ws.getRow(2).height = 6;

      const enc = datosEncabezado || {};
      const partes = (enc.numero_y_nombre_completo || '').split(' - ');
      const numEmpleado = (partes[0] || '').trim();
      const nombreEmpleado = (partes.slice(1).join(' - ') || '').trim();
      const formatFecha = (val) => {
        if (!val) return '';
        const d = new Date(val);
        if (Number.isNaN(d.getTime())) return String(val);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };

      const headerRows = [
        ['NUMERO DE EMPLEADO', numEmpleado],
        ['NOMBRE SERVIDOR PUBLICO', nombreEmpleado],
        ['CARGO', enc.puesto_clave_y_descripcion || ''],
        ['LUGAR DE ADSCRIPCION', enc.directorio_de_area_clave_y_nombre || ''],
        ['FECHA DE ALTA', formatFecha(enc.fecha_de_alta)],
        ['FECHA DE BAJA', formatFecha(enc.fecha_de_baja)],
        ['MOTIVO DE BAJA', enc.motivo_baja_concepto || ''],
      ];
      headerRows.forEach(([lbl, val], idx) => {
        const row = ws.getRow(3 + idx);
        row.getCell(2).value = lbl;
        row.getCell(2).font = labelFont;
        ws.mergeCells(3 + idx, 3, 3 + idx, 5);
        row.getCell(3).value = val;
        row.getCell(3).font = valueFont;
      });

      ws.getRow(10).height = 6;

      // -- Titulo del calculo ISR (fila 11+) --
      const titleText = `CALCULO OPCIONAL DE ISR DE ${item.concepto.toUpperCase()}`;
      ws.mergeCells('A11:E11');
      const titleCell = ws.getCell('A11');
      titleCell.value = titleText;
      titleCell.font = titleFont;
      titleCell.fill = titleFill;
      titleCell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
      ws.getRow(11).height = 28;

      ws.getRow(12).height = 6;

      ws.getCell('C13').value = 'Importe';
      ws.getCell('C13').font = boldFont;
      ws.getCell('C13').alignment = { horizontal: 'center' };
      ws.getCell('D13').value = 'Sueldo Mensual';
      ws.getCell('D13').font = boldFont;
      ws.getCell('D13').alignment = { horizontal: 'center' };

      const addRow = (rowNum, op, label, val, valSueldo, note, opts = {}) => {
        const rObj = ws.getRow(rowNum);
        rObj.getCell(1).value = op || null;
        rObj.getCell(1).font = normalFont;
        rObj.getCell(1).alignment = { horizontal: 'center' };
        rObj.getCell(2).value = label || null;
        rObj.getCell(2).font = opts.bold ? boldFont : normalFont;
        if (val !== null && val !== undefined) {
          rObj.getCell(3).value = val;
          rObj.getCell(3).font = opts.bold ? boldFont : normalFont;
          rObj.getCell(3).alignment = { horizontal: 'right' };
          rObj.getCell(3).numFmt = opts.numFmt || fmtMoney;
        }
        if (valSueldo !== null && valSueldo !== undefined) {
          rObj.getCell(4).value = valSueldo;
          rObj.getCell(4).font = opts.bold ? boldFont : normalFont;
          rObj.getCell(4).alignment = { horizontal: 'right' };
          rObj.getCell(4).numFmt = opts.numFmtSueldo || opts.numFmt || fmtMoney;
        }
        if (note) {
          rObj.getCell(5).value = note;
          rObj.getCell(5).font = {
            ...normalFont,
            italic: true,
            color: { argb: 'FF666666' },
          };
        }
        if (opts.highlight) {
          for (let c = 1; c <= 5; c += 1) {
            rObj.getCell(c).fill = highlightFill;
          }
        }
        if (opts.border) {
          for (let c = 1; c <= 4; c += 1) {
            rObj.getCell(c).border = thinBorder;
          }
        }
      };

      const s = sueldoEntry || {};
      let r = 14;
      addRow(r, null, item.concepto, item.importe, null, null, {
        bold: true,
        border: true,
      });
      r += 1;
      addRow(r, '( - )', 'Exento', item.exento, null, item.exento_umas_texto || null, { border: true });
      r += 1;
      addRow(r, '=', 'Importe Gravable', item.gravado, null, null, {
        bold: true,
        border: true,
      });
      r += 1;
      addRow(r, '( / )', 'Entre', 365, null, null, { border: true });
      r += 1;
      addRow(r, '=', 'Promedio Diario', item.promedio_diario, null, null, {
        border: true,
        numFmt: fmtDec6,
      });
      r += 1;
      addRow(r, '( x )', 'Por', 30.4, null, null, { border: true });
      r += 1;
      addRow(r, '=', 'Promedio Mensual', item.promedio_mensual, null, null, { border: true });
      r += 1;
      addRow(r, '( + )', 'Sueldo Mensual', item.sueldo_mensual, item.sueldo_mensual, null, { border: true });
      r += 1;
      addRow(r, '=', 'Base Gravable', item.base_gravable, s.base_gravable, null, {
        bold: true,
        border: true,
      });
      r += 1;
      addRow(r, '( - )', 'Limite Inferior', item.limite_inferior, s.limite_inferior, null, { border: true });
      r += 1;
      addRow(r, '=', 'Excedente', item.excedente, s.excedente, null, { border: true });
      r += 1;
      addRow(r, '( * )', '% sobre Excedente', item.porcentaje_excedente / 100, s.porcentaje_excedente != null ? s.porcentaje_excedente / 100 : null, null, {
        border: true,
        numFmt: fmtPct,
        numFmtSueldo: fmtPct,
      });
      r += 1;
      addRow(r, '=', 'Impuesto Marginal', item.impuesto_marginal, s.impuesto_marginal, null, { border: true });
      r += 1;
      addRow(r, '( + )', 'Cuota Fija', item.cuota_fija, s.cuota_fija, null, { border: true });
      r += 1;
      addRow(r, '=', 'ISR Causado', item.isr_causado, s.isr_causado, null, {
        bold: true,
        border: true,
      });
      r += 1;
      r += 1;
      addRow(r, '( - )', 'Subsidio al Empleo', item.subsidio_empleo || 0, s.subsidio_empleo || 0, null, { border: true });
      r += 1;
      r += 1;
      addRow(r, null, 'ISR Causado', item.isr_causado, s.isr_causado, null, {
        bold: true,
        border: true,
      });
      r += 1;
      r += 1;
      addRow(r, '( - )', 'ISR del Sueldo Mensual', item.isr_sueldo_mensual, null, null, { border: true });
      r += 1;
      r += 1;
      addRow(r, '=', 'ISR Adicional', item.isr_adicional, null, null, {
        bold: true,
        border: true,
      });
      r += 1;
      r += 1;
      addRow(r, '( / )', 'Ingreso Mensualizado', item.promedio_mensual, null, null, { border: true });
      r += 1;
      r += 1;
      addRow(r, '=', 'Tasa Real de Impuesto', item.tasa_real_impuesto, null, null, {
        bold: true,
        border: true,
        numFmt: fmtPct4,
      });
      r += 1;
      r += 1;
      addRow(r, '( x )', 'Importe Gravable', item.gravado, null, null, { border: true });
      r += 1;
      r += 1;
      addRow(r, '=', 'ISR del Importe Gravable', item.importe_isr, null, null, {
        bold: true,
        border: true,
        highlight: true,
      });
      r += 1;
      r += 1;
      addRow(r, '( - )', 'ISR del Importe Gravable que paga el Patron', 0, null, null, { border: true });
      r += 1;
      r += 1;
      addRow(r, '=', 'ISR Neto que paga el Patron', item.importe_isr, null, null, {
        bold: true,
        border: true,
        highlight: true,
      });
    };

    if (irregulares.length) {
      irregulares.forEach((item) => buildSheet(item));
    } else if (sueldoEntry) {
      const ws = workbook.addWorksheet('ISR.SUELDO.MENSUAL');
      ws.getColumn(1).width = 10;
      ws.getColumn(2).width = 42;
      ws.getColumn(3).width = 22;
      ws.getColumn(4).width = 50;

      // -- Cabecera FINIQUITO (filas 1-10) --
      const sHeaderFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF004080' },
      };
      const sHeaderFont = {
        name: 'Calibri',
        size: 13,
        bold: true,
        color: { argb: 'FFFFFFFF' },
      };
      const sLabelFont = {
        name: 'Calibri',
        size: 11,
        bold: true,
      };
      const sValueFont = {
        name: 'Calibri',
        size: 11,
      };

      ws.mergeCells('A1:D1');
      const sFiniquitoCell = ws.getCell('A1');
      sFiniquitoCell.value = 'FINIQUITO';
      sFiniquitoCell.font = sHeaderFont;
      sFiniquitoCell.fill = sHeaderFill;
      sFiniquitoCell.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.getRow(1).height = 28;

      ws.getRow(2).height = 6;

      const sEnc = datosEncabezado || {};
      const sPartes = (sEnc.numero_y_nombre_completo || '').split(' - ');
      const sNumEmpleado = (sPartes[0] || '').trim();
      const sNombreEmpleado = (sPartes.slice(1).join(' - ') || '').trim();
      const sFormatFecha = (val) => {
        if (!val) return '';
        const d = new Date(val);
        if (Number.isNaN(d.getTime())) return String(val);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };

      const sHeaderRows = [
        ['NUMERO DE EMPLEADO', sNumEmpleado],
        ['NOMBRE SERVIDOR PUBLICO', sNombreEmpleado],
        ['CARGO', sEnc.puesto_clave_y_descripcion || ''],
        ['LUGAR DE ADSCRIPCION', sEnc.directorio_de_area_clave_y_nombre || ''],
        ['FECHA DE ALTA', sFormatFecha(sEnc.fecha_de_alta)],
        ['FECHA DE BAJA', sFormatFecha(sEnc.fecha_de_baja)],
        ['MOTIVO DE BAJA', sEnc.motivo_baja_concepto || ''],
      ];
      sHeaderRows.forEach(([lbl, val], idx) => {
        const row = ws.getRow(3 + idx);
        row.getCell(2).value = lbl;
        row.getCell(2).font = sLabelFont;
        ws.mergeCells(3 + idx, 3, 3 + idx, 4);
        row.getCell(3).value = val;
        row.getCell(3).font = sValueFont;
      });

      ws.getRow(10).height = 6;

      // -- Titulo ISR Sueldo Mensual (fila 11+) --
      ws.mergeCells('A11:D11');
      const tc = ws.getCell('A11');
      tc.value = 'CALCULO DE ISR DE SUELDO MENSUAL';
      tc.font = titleFont;
      tc.fill = titleFill;
      tc.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
      ws.getRow(11).height = 28;

      const sAddRow = (rowNum, op, label, val, note, opts = {}) => {
        const rObj = ws.getRow(rowNum);
        rObj.getCell(1).value = op || null;
        rObj.getCell(1).font = normalFont;
        rObj.getCell(1).alignment = { horizontal: 'center' };
        rObj.getCell(2).value = label || null;
        rObj.getCell(2).font = opts.bold ? boldFont : normalFont;
        if (val !== null && val !== undefined) {
          rObj.getCell(3).value = val;
          rObj.getCell(3).font = opts.bold ? boldFont : normalFont;
          rObj.getCell(3).alignment = { horizontal: 'right' };
          rObj.getCell(3).numFmt = opts.numFmt || fmtMoney;
        }
        if (note) {
          rObj.getCell(4).value = note;
          rObj.getCell(4).font = {
            ...normalFont,
            italic: true,
            color: { argb: 'FF666666' },
          };
        }
        if (opts.highlight) {
          for (let c = 1; c <= 4; c += 1) {
            rObj.getCell(c).fill = highlightFill;
          }
        }
        if (opts.border) {
          for (let c = 1; c <= 3; c += 1) {
            rObj.getCell(c).border = thinBorder;
          }
        }
      };

      const s = sueldoEntry;
      sAddRow(13, null, 'Sueldo Mensual', s.gravado, null, {
        bold: true,
        border: true,
      });
      sAddRow(14, '=', 'Base Gravable', s.base_gravable, null, {
        bold: true,
        border: true,
      });
      sAddRow(15, '( - )', 'Limite Inferior', s.limite_inferior, null, { border: true });
      sAddRow(16, '=', 'Excedente', s.excedente, null, { border: true });
      sAddRow(17, '( * )', '% sobre Excedente', s.porcentaje_excedente / 100, null, {
        border: true,
        numFmt: fmtPct,
      });
      sAddRow(18, '=', 'Impuesto Marginal', s.impuesto_marginal, null, { border: true });
      sAddRow(19, '( + )', 'Cuota Fija', s.cuota_fija, null, { border: true });
      sAddRow(20, '=', 'ISR Causado', s.isr_causado, null, {
        bold: true,
        border: true,
      });
      sAddRow(22, '( - )', 'Subsidio al Empleo', s.subsidio_empleo || 0, null, { border: true });
      sAddRow(24, '=', 'ISR del Sueldo Mensual', s.importe_isr, null, {
        bold: true,
        border: true,
        highlight: true,
      });
      sAddRow(26, null, null, null, 'Las percepciones irregulares de esta liquidacion son 100% exentas; no generan ISR adicional por tasa real.');
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Cedula_Trabajo_ISR_Liquidacion_${liquidacionEmpleado}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          name="general"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onFinish();
            }
          }}
        >
          <EditableTable
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            allowSearch={false}
            controls={{
              onClickFinish: () => onFinish(),
              onClickCancel: () => onCancel(),
            }}
            summaryCells={summaryCells}
            summaryColumnPosition={2}
            permission={permission}
          />
          <Col xs={24} sm={24} md={5}>
            <CurrencyInput
              name="importe_deducciones"
              label="Importe de Deducciones"
              disabled
            />
          </Col>
        </Form>
        {deduccionConDetalleISR ? (
          <div style={{ marginTop: 24 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
            >
              <h4 style={{ margin: 0 }}>Detalle del Cálculo de ISR</h4>
              <Button
                icon={<FileExcelOutlined />}
                size="small"
                onClick={exportarCedulaDeTrabajo}
              >
                Cedula de Trabajo
              </Button>
            </div>
            <AntTable
              rowKey="concepto"
              columns={columnasDetalleISRConceptos}
              dataSource={deduccionConDetalleISR.detalle_isr}
              pagination={false}
              size="small"
              style={{ marginBottom: 12 }}
            />
            <AntTable
              rowKey="concepto"
              columns={columnasDetalleISRBaseGravable}
              dataSource={deduccionConDetalleISR.detalle_isr}
              pagination={false}
              size="small"
            />
            {deduccionConDetalleISR.detalle_isr
              .filter((item) => item.promedio_diario !== null && item.promedio_diario !== undefined)
              .map((item) => {
                const rows = buildDetalleISRRows(item);
                return (
                  <div key={item.concepto} style={{ marginTop: 24 }}>
                    <h4 style={{ marginBottom: 8 }}>
                      Cálculo Opcional de ISR de
                      {' '}
                      {item.concepto}
                    </h4>
                    <AntTable
                      rowKey="key"
                      columns={[
                        {
                          title: '',
                          dataIndex: 'label',
                          key: 'label',
                          width: 280,
                          render: (val, record) => (
                            <span style={{ fontWeight: record.bold ? 'bold' : 'normal' }}>
                              {val}
                            </span>
                          ),
                        },
                        {
                          title: '',
                          dataIndex: 'valor',
                          key: 'valor',
                          align: 'right',
                          width: 160,
                          render: (val, record) => {
                            if (val === null || val === undefined) return '-';
                            let text;
                            if (record.format === 'pct') {
                              text = `${Number(val).toFixed(2)}%`;
                            } else if (record.format === 'pct4') {
                              text = `${(Number(val) * 100).toFixed(4)}%`;
                            } else {
                              text = currencyFormatter(val);
                            }
                            return (
                              <span
                                style={{
                                  fontWeight: record.bold ? 'bold' : 'normal',
                                  backgroundColor: record.highlight ? '#e6f7ff' : undefined,
                                  padding: record.highlight ? '2px 6px' : undefined,
                                }}
                              >
                                {text}
                              </span>
                            );
                          },
                        },
                        {
                          title: '',
                          dataIndex: 'extra',
                          key: 'extra',
                          render: (val) => (val ? <span style={{ color: '#888', fontSize: 12 }}>{val}</span> : null),
                        },
                      ]}
                      dataSource={rows}
                      pagination={false}
                      size="small"
                      showHeader={false}
                      bordered
                    />
                  </div>
                );
              })}
          </div>
        ) : null}
      </Spin>
    </Row>
  );
};

export default LiquidacionesEmpleadosDeducciones;
